import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';
import { sortRegions } from '../utils/sort';
import PageLayout from '../components/PageLayout';
import EnrichmentEditor from '../components/EnrichmentEditor';
import { useNavigate } from 'react-router-dom';
import {
  fetchMapMarkerCdnOptions,
  flattenMapMarkerOptions,
  type MapMarkerCdnGrouped,
} from '../utils/mapMarkerCdn';
import {
  BrandingSelectPreview,
  VenueBrandingPair,
  VenueNameWithBranding,
} from '../components/VenueBrandingPreview';
import './VenuesPage.css';

interface ManualFieldUpdate {
  field_name: string;
  old_value: any;
  new_value: any;
  mod_date: string;
}

interface Venue {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  address: string;
  address_2?: string;
  city: string;
  state: string;
  zip_code?: string;
  map_marker?: string;
  venue_icon?: string;
  region_name?: string;
  capacity?: number | null; // null clears (and locks) on save
  capacity_source?: string; // "eventim" or "admin"
  neighborhood?: string;
  neighborhood_source?: string; // "polygon", "city_fallback", or "admin"
  source?: string; // "ticketmaster", "etix", "eventim", "scraper", or undefined (none)
  last_updated?: string;
  created_at?: string;
  manually_updated_fields?: ManualFieldUpdate[];
  is_deleted?: boolean;
  venue_group_id?: string;
  venue_group_map_marker?: string;
  venue_group_venue_icon?: string;
}

function venueDisplayBranding(venue: Venue): { mapMarker?: string; venueIcon?: string } {
  if (venue.venue_group_id) {
    return {
      mapMarker: venue.venue_group_map_marker,
      venueIcon: venue.venue_group_venue_icon,
    };
  }
  return { mapMarker: venue.map_marker, venueIcon: venue.venue_icon };
}

const VenuesPage: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [formData, setFormData] = useState<Partial<Venue>>({});
  const [showEnrichmentEditor, setShowEnrichmentEditor] = useState(false);
  const [enrichmentEntityId, setEnrichmentEntityId] = useState<string>('');
  const [enrichmentType, setEnrichmentType] = useState<'ticketmaster' | 'etix' | 'eventim'>('ticketmaster');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('all'); // 'all', 'ticketmaster', 'etix', 'eventim', 'scraper', 'none'
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [regions, setRegions] = useState<Array<{ region_name: string; metro_name: string }>>([]);
  const [mapMarkerGroups, setMapMarkerGroups] = useState<MapMarkerCdnGrouped>({
    shared: [],
    regional: [],
  });
  const [loadingMapMarkers, setLoadingMapMarkers] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showDeleted, setShowDeleted] = useState<boolean>(false);
  const [unlockingField, setUnlockingField] = useState<{ venueId: string; fieldName: string } | null>(null);
  const [expandedVenueId, setExpandedVenueId] = useState<string | null>(null);
  const [selectedVenueIds, setSelectedVenueIds] = useState<Set<string>>(new Set());
  const [venueGroupCache, setVenueGroupCache] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (token) {
      loadVenues();
      loadRegions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) {
      loadVenues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionFilter, token]);

  useEffect(() => {
    if (!token || !showForm) return;
    const regionSlug =
      (formData.region_name && formData.region_name.trim()) ||
      (editingVenue?.region_name && editingVenue.region_name.trim()) ||
      (regionFilter !== 'all' ? regionFilter : undefined);
    let cancelled = false;
    (async () => {
      setLoadingMapMarkers(true);
      try {
        const grouped = await fetchMapMarkerCdnOptions(token, regionSlug || undefined);
        if (!cancelled) setMapMarkerGroups(grouped);
      } catch (err) {
        console.error('Failed to load map markers:', err);
        if (!cancelled) setMapMarkerGroups({ shared: [], regional: [] });
      } finally {
        if (!cancelled) setLoadingMapMarkers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    token,
    showForm,
    formData.region_name,
    editingVenue?.region_name,
    editingVenue?.id,
    regionFilter,
  ]);

  const loadRegions = async () => {
    if (!token) return;
    try {
      const response = await apiRequest<{ regions: Array<{ region_name: string; metro_name: string }> | null; count: number }>(
        '/admin/regions',
        token
      );
      setRegions(sortRegions(response.regions || []));
    } catch (err: any) {
      console.error('Failed to load regions:', err);
      setRegions([]);
    }
  };

  const loadVenues = async () => {
    if (!token) return;
    try {
      setLoading(true);
      let url = '/admin/venues';
      if (regionFilter !== 'all') {
        url += `?region=${encodeURIComponent(regionFilter)}`;
      }
      const response = await apiRequest<{ venues: Venue[] | null; count: number }>(
        url,
        token
      );
      const loadedVenues = response.venues || [];
      setVenues(loadedVenues);
      
      // Build venue group cache from venue_group_id in response
      const groupCache = new Map<string, string>();
      loadedVenues.forEach((venue) => {
        if (venue.venue_group_id) {
          groupCache.set(venue.id, venue.venue_group_id);
        }
      });
      setVenueGroupCache(groupCache);
      
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load venues');
      setVenues([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingVenue(null);
    setFormData(regionFilter !== 'all' ? { region_name: regionFilter } : {});
    setShowForm(true);
  };

  const handleToggleVenueSelection = (venueId: string) => {
    const newSelection = new Set(selectedVenueIds);
    if (newSelection.has(venueId)) {
      newSelection.delete(venueId);
    } else {
      newSelection.add(venueId);
    }
    setSelectedVenueIds(newSelection);
  };

  const handleSelectAll = () => {
    const filtered = getFilteredVenues();
    if (selectedVenueIds.size === filtered.length) {
      setSelectedVenueIds(new Set());
    } else {
      setSelectedVenueIds(new Set(filtered.map(v => v.id)));
    }
  };

  const handleCreateVenueGroup = () => {
    if (selectedVenueIds.size >= 2) {
      const venueIdsArray = Array.from(selectedVenueIds);
      navigate(`/venues/groups?venue_ids=${venueIdsArray.join(',')}`);
    }
  };

  const handleViewVenueGroup = (venueId: string) => {
    const groupId = venueGroupCache.get(venueId);
    if (groupId) {
      navigate(`/venues/groups?venue_group_id=${groupId}`);
    }
  };

  const handleEdit = (venue: Venue) => {
    setEditingVenue(venue);
    setFormData(venue);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!token || !window.confirm('Are you sure you want to delete this venue? This will soft-delete it (mark as deleted).')) {
      return;
    }
    try {
      await apiRequest(`/admin/venues/${id}`, token, { method: 'DELETE' });
      await loadVenues();
    } catch (err: any) {
      setError(err.message || 'Failed to delete venue');
    }
  };

  const handleHardDelete = async (id: string) => {
    if (!token || !window.confirm('Are you sure you want to permanently delete this venue? This cannot be undone and will allow ingestion to recreate it.')) {
      return;
    }
    try {
      await apiRequest(`/admin/venues/${id}/hard`, token, { method: 'DELETE' });
      await loadVenues();
    } catch (err: any) {
      setError(err.message || 'Failed to hard delete venue');
    }
  };

  const handleUnlockField = async (venueId: string, fieldName: string) => {
    if (!token || !window.confirm(`Are you sure you want to unlock the "${fieldName}" field? This will allow ingestion to update it again.`)) {
      return;
    }
    try {
      setUnlockingField({ venueId, fieldName });
      await apiRequest(`/admin/venues/${venueId}/unlock?field=${encodeURIComponent(fieldName)}`, token, { method: 'POST' });
      await loadVenues();
      setUnlockingField(null);
    } catch (err: any) {
      setError(err.message || 'Failed to unlock field');
      setUnlockingField(null);
    }
  };

  const isFieldProtected = (venue: Venue, fieldName: string): boolean => {
    return venue.manually_updated_fields?.some(update => update.field_name === fieldName) || false;
  };

  const getFieldUpdate = (venue: Venue, fieldName: string): ManualFieldUpdate | undefined => {
    return venue.manually_updated_fields?.find(update => update.field_name === fieldName);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    if (!editingVenue && !formData.region_name?.trim()) {
      setError('Region is required');
      return;
    }

    try {
      // Ensure latitude and longitude are properly typed as numbers
      const payload = {
        ...formData,
        latitude: formData.latitude ? Number(formData.latitude) : undefined,
        longitude: formData.longitude ? Number(formData.longitude) : undefined,
      };
      
      // Log what we're sending for debugging
      console.log('Submitting venue with latitude:', payload.latitude, 'longitude:', payload.longitude);
      
      if (editingVenue) {
        await apiRequest(`/admin/venues/${editingVenue.id}`, token, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await apiRequest('/admin/venues', token, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      setShowForm(false);
      await loadVenues();
    } catch (err: any) {
      setError(err.message || 'Failed to save venue');
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? '▲' : '▼';
  };

  const getFilteredVenues = () => {
    let filtered = venues;
    
    // Filter by deleted status (default: exclude deleted)
    if (!showDeleted) {
      filtered = filtered.filter(venue => !venue.is_deleted);
    }
    
    // Filter by source
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(venue => {
        if (sourceFilter === 'none') {
          return !venue.source || venue.source === '';
        }
        return venue.source === sourceFilter;
      });
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(venue => {
        const nameMatch = venue.name?.toLowerCase().includes(term);
        const idMatch = venue.id?.toLowerCase().includes(term);
        const cityMatch = venue.city?.toLowerCase().includes(term);
        const stateMatch = venue.state?.toLowerCase().includes(term);
        return nameMatch || idMatch || cityMatch || stateMatch;
      });
    }
    
    // Sort the filtered results
    if (!sortColumn || filtered.length === 0) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'id':
          aValue = a.id?.toLowerCase() || '';
          bValue = b.id?.toLowerCase() || '';
          break;
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'city':
          aValue = a.city?.toLowerCase() || '';
          bValue = b.city?.toLowerCase() || '';
          break;
        case 'state':
          aValue = a.state?.toLowerCase() || '';
          bValue = b.state?.toLowerCase() || '';
          break;
        case 'source':
          aValue = a.source?.toLowerCase() || '';
          bValue = b.source?.toLowerCase() || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const effectiveMapMarkerRegionSlug =
    formData.region_name?.trim() ||
    editingVenue?.region_name?.trim() ||
    (regionFilter !== 'all' ? regionFilter : '');

  if (loading && venues.length === 0) {
    return (
      <PageLayout title="Venues">
        <div>Loading...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Venues">
      {error && <div className="error-banner">{error}</div>}
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flex: 1, maxWidth: '600px' }}>
          <div style={{ flex: 1 }}>
            <input
              type="text"
              placeholder="Search by name, city, state, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            />
          </div>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            style={{
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              minWidth: '150px'
            }}
          >
            <option value="all">All Sources</option>
            <option value="etix">Etix</option>
            <option value="eventim">EventIM</option>
            <option value="scraper">Scraper</option>
            <option value="ticketmaster">Ticketmaster</option>
            <option value="none">No Source</option>
          </select>
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            style={{
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              minWidth: '150px'
            }}
          >
            <option value="all">All Regions</option>
            {regions.map((region) => (
              <option key={region.region_name} value={region.region_name}>
                {region.metro_name}
              </option>
            ))}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            <span>Show Deleted</span>
          </label>
        </div>
        <button onClick={handleCreate} className="btn-primary">
          Create Venue
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <VenueNameWithBranding
              name={formData.name || (editingVenue ? 'Edit Venue' : 'Create Venue')}
              mapMarker={
                editingVenue?.venue_group_id
                  ? editingVenue.venue_group_map_marker
                  : formData.map_marker
              }
              venueIcon={
                editingVenue?.venue_group_id
                  ? editingVenue.venue_group_venue_icon
                  : formData.venue_icon
              }
              subtitle={
                editingVenue?.venue_group_id
                  ? `In venue group ${editingVenue.venue_group_id} — previews show group branding`
                  : undefined
              }
            />
            {editingVenue?.venue_group_id && (
              <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '-0.5rem', marginBottom: '1rem' }}>
                Map and list/calendar icons for this venue come from its{' '}
                <button
                  type="button"
                  className="btn-link"
                  style={{ padding: 0, fontSize: 'inherit' }}
                  onClick={() => navigate(`/venues/groups?venue_group_id=${editingVenue.venue_group_id}`)}
                >
                  venue group
                </button>
                . Edit branding on the Venue Grouping page.
              </p>
            )}
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Region *</label>
                <select
                  value={formData.region_name || ''}
                  onChange={(e) => setFormData({ ...formData, region_name: e.target.value })}
                  required
                >
                  <option value="">-- Select Region --</option>
                  {regions.map((region) => (
                    <option key={region.region_name} value={region.region_name}>
                      {region.metro_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={formData.state || ''}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Zip Code</label>
                  <input
                    type="text"
                    value={formData.zip_code || ''}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Latitude</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={formData.latitude ?? ''}
                    onChange={(e) => {
                      const val = e.target.value.trim();
                      // Allow empty, numbers, decimals, and negative signs
                      if (val === '' || /^-?\d*\.?\d*$/.test(val)) {
                        if (val === '') {
                          setFormData(prev => ({ ...prev, latitude: undefined }));
                        } else {
                          const numVal = parseFloat(val);
                          if (!isNaN(numVal)) {
                            setFormData(prev => ({ ...prev, latitude: numVal }));
                          }
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Ensure final value is a valid number
                      const val = e.target.value.trim();
                      if (val !== '' && val !== String(formData.latitude ?? '')) {
                        const numVal = parseFloat(val);
                        if (!isNaN(numVal)) {
                          setFormData(prev => ({ ...prev, latitude: numVal }));
                        } else if (val !== '') {
                          // Invalid number, reset to current formData value
                          e.target.value = String(formData.latitude ?? '');
                        }
                      }
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>Longitude</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={formData.longitude ?? ''}
                    onChange={(e) => {
                      const val = e.target.value.trim();
                      // Allow empty, numbers, decimals, and negative signs
                      if (val === '' || /^-?\d*\.?\d*$/.test(val)) {
                        if (val === '') {
                          setFormData(prev => ({ ...prev, longitude: undefined }));
                        } else {
                          const numVal = parseFloat(val);
                          if (!isNaN(numVal)) {
                            setFormData(prev => ({ ...prev, longitude: numVal }));
                          }
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Ensure final value is a valid number
                      const val = e.target.value.trim();
                      if (val !== '' && val !== String(formData.longitude ?? '')) {
                        const numVal = parseFloat(val);
                        if (!isNaN(numVal)) {
                          setFormData(prev => ({ ...prev, longitude: numVal }));
                        } else if (val !== '') {
                          // Invalid number, reset to current formData value
                          e.target.value = String(formData.longitude ?? '');
                        }
                      }
                    }}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address || ''}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    Capacity
                    {formData.capacity_source && (
                      <span style={{ color: '#999', fontWeight: 'normal' }}> ({formData.capacity_source})</span>
                    )}
                  </label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={formData.capacity ?? ''}
                    onChange={(e) => {
                      const val = e.target.value.trim();
                      setFormData(prev => ({ ...prev, capacity: val === '' ? null : Math.round(Number(val)) }));
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>
                    Neighborhood
                    {formData.neighborhood_source && (
                      <span style={{ color: '#999', fontWeight: 'normal' }}> ({formData.neighborhood_source.replace('_', ' ')})</span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={formData.neighborhood || ''}
                    onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                  />
                </div>
              </div>
              {loadingMapMarkers ? (
                <div className="form-group">Loading map markers...</div>
              ) : (
                <>
                  <BrandingSelectPreview label="Venue icon (list & calendar)" value={formData.venue_icon}>
                    <select
                      value={formData.venue_icon || ''}
                      onChange={(e) => setFormData({ ...formData, venue_icon: e.target.value })}
                    >
                      <option value="">-- Select venue icon --</option>
                      {formData.venue_icon &&
                        !flattenMapMarkerOptions(mapMarkerGroups).some(
                          (o) => o.value === formData.venue_icon
                        ) && (
                          <option value={formData.venue_icon}>
                            {formData.venue_icon} (current, not in list)
                          </option>
                        )}
                      <optgroup label="CDN root (shared — includes default icon)">
                        {mapMarkerGroups.shared.map((opt) => (
                          <option key={`vi-${opt.value}`} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </optgroup>
                      {mapMarkerGroups.regional.length > 0 && (
                        <optgroup label={`Regional (${effectiveMapMarkerRegionSlug})`}>
                          {mapMarkerGroups.regional.map((opt) => (
                            <option key={`vi-r-${opt.value}`} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </BrandingSelectPreview>
                  <small style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginTop: '-0.5rem', marginBottom: '1rem' }}>
                    Primary graphic for list and calendar; map pin may use map marker override below.
                  </small>

                  <BrandingSelectPreview label="Map marker (pin override)" value={formData.map_marker}>
                    <select
                      value={formData.map_marker || ''}
                      onChange={(e) => setFormData({ ...formData, map_marker: e.target.value })}
                    >
                      <option value="">-- Select map marker --</option>
                      {formData.map_marker &&
                        !flattenMapMarkerOptions(mapMarkerGroups).some(
                          (o) => o.value === formData.map_marker
                        ) && (
                          <option value={formData.map_marker}>
                            {formData.map_marker} (current, not in list)
                          </option>
                        )}
                      <optgroup label="CDN root (shared — includes default icon)">
                        {mapMarkerGroups.shared.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </optgroup>
                      {mapMarkerGroups.regional.length > 0 && (
                        <optgroup label={`Regional (${effectiveMapMarkerRegionSlug})`}>
                          {mapMarkerGroups.regional.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </BrandingSelectPreview>
                  <small style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginTop: '-0.5rem', marginBottom: '1rem' }}>
                    Optional different asset for map pins.
                    {!formData.region_name?.trim() &&
                      !editingVenue?.region_name?.trim() &&
                      regionFilter === 'all' &&
                      ' Select a region to also list that region’s folder.'}
                  </small>
                </>
              )}
              <div className="form-actions">
                <button type="button" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingVenue ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedVenueIds.size >= 2 && (
        <div className="create-venue-group-button">
          <button className="btn-primary" onClick={handleCreateVenueGroup}>
            Create Venue Group ({selectedVenueIds.size} selected)
          </button>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={selectedVenueIds.size > 0 && selectedVenueIds.size === getFilteredVenues().length}
                  onChange={handleSelectAll}
                  title="Select all venues"
                />
              </th>
              <th 
                onClick={() => handleSort('id')}
                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
              >
                ID {getSortIcon('id') && <span className="sort-arrow">{getSortIcon('id')}</span>}
              </th>
              <th 
                onClick={() => handleSort('name')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Name {getSortIcon('name') && <span className="sort-arrow">{getSortIcon('name')}</span>}
              </th>
              <th 
                onClick={() => handleSort('city')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                City {getSortIcon('city') && <span className="sort-arrow">{getSortIcon('city')}</span>}
              </th>
              <th 
                onClick={() => handleSort('state')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                State {getSortIcon('state') && <span className="sort-arrow">{getSortIcon('state')}</span>}
              </th>
              <th 
                onClick={() => handleSort('source')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Source {getSortIcon('source') && <span className="sort-arrow">{getSortIcon('source')}</span>}
              </th>
              <th>Coordinates</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {getFilteredVenues().length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  {loading ? 'Loading...' : 'No venues found. Create your first venue to get started.'}
                </td>
              </tr>
            ) : (
              getFilteredVenues().map((venue) => (
              <React.Fragment key={venue.id}>
              <tr style={{ opacity: venue.is_deleted ? 0.6 : 1 }}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedVenueIds.has(venue.id)}
                    onChange={() => handleToggleVenueSelection(venue.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td>{venue.id}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {(() => {
                      const branding = venueDisplayBranding(venue);
                      return (
                        <VenueBrandingPair
                          mapMarker={branding.mapMarker}
                          venueIcon={branding.venueIcon}
                        />
                      );
                    })()}
                    {venue.name}
                    {venueGroupCache.has(venue.id) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewVenueGroup(venue.id);
                        }}
                        className="btn-venue-group"
                        title="View Venue Group"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          color: '#667eea'
                        }}
                      >
                        <span style={{ fontSize: '14px' }}>☰</span>
                      </button>
                    )}
                    {isFieldProtected(venue, 'name') && (
                      <span
                        title={`Protected field. Changed from "${getFieldUpdate(venue, 'name')?.old_value}" to "${getFieldUpdate(venue, 'name')?.new_value}" on ${new Date(getFieldUpdate(venue, 'name')?.mod_date || '').toLocaleString()}`}
                        style={{ cursor: 'help', color: '#ff9800' }}
                      >
                        🔒
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {venue.city}
                    {isFieldProtected(venue, 'city') && (
                      <span
                        title={`Protected field. Changed from "${getFieldUpdate(venue, 'city')?.old_value}" to "${getFieldUpdate(venue, 'city')?.new_value}" on ${new Date(getFieldUpdate(venue, 'city')?.mod_date || '').toLocaleString()}`}
                        style={{ cursor: 'help', color: '#ff9800' }}
                      >
                        🔒
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {venue.state}
                    {isFieldProtected(venue, 'state') && (
                      <span
                        title={`Protected field. Changed from "${getFieldUpdate(venue, 'state')?.old_value}" to "${getFieldUpdate(venue, 'state')?.new_value}" on ${new Date(getFieldUpdate(venue, 'state')?.mod_date || '').toLocaleString()}`}
                        style={{ cursor: 'help', color: '#ff9800' }}
                      >
                        🔒
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  {venue.source ? (
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      backgroundColor: venue.source === 'ticketmaster' ? '#e3f2fd' : venue.source === 'scraper' ? '#e8f5e9' : '#fff3e0',
                      color: venue.source === 'ticketmaster' ? '#1976d2' : venue.source === 'scraper' ? '#2e7d32' : '#e65100',
                      textTransform: 'capitalize'
                    }}>
                      {venue.source === 'eventim' ? 'EventIM' : venue.source === 'scraper' ? 'Scraper' : venue.source}
                    </span>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )}
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {venue.latitude && venue.longitude
                      ? `${venue.latitude.toFixed(4)}, ${venue.longitude.toFixed(4)}`
                      : '-'}
                    {(isFieldProtected(venue, 'latitude') || isFieldProtected(venue, 'longitude')) && (
                      <span
                        title={`Protected coordinates. Changed on ${new Date(getFieldUpdate(venue, 'latitude')?.mod_date || getFieldUpdate(venue, 'longitude')?.mod_date || '').toLocaleString()}`}
                        style={{ cursor: 'help', color: '#ff9800' }}
                      >
                        🔒
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  {venue.is_deleted ? (
                    <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>Deleted</span>
                  ) : (
                    <span style={{ color: '#2e7d32' }}>Active</span>
                  )}
                </td>
                <td>
                  <button onClick={() => handleEdit(venue)} className="btn-sm">
                    Edit
                  </button>
                  {venue.source === 'ticketmaster' && (
                    <button
                      onClick={() => {
                        setEnrichmentEntityId(venue.id);
                        setEnrichmentType('ticketmaster');
                        setShowEnrichmentEditor(true);
                      }}
                      className="btn-sm btn-enrichment"
                    >
                      Edit Ticketmaster
                    </button>
                  )}
                  {venue.source === 'etix' && (
                    <button
                      onClick={() => {
                        setEnrichmentEntityId(venue.id);
                        setEnrichmentType('etix');
                        setShowEnrichmentEditor(true);
                      }}
                      className="btn-sm btn-enrichment"
                    >
                      Edit Etix
                    </button>
                  )}
                  {venue.source === 'eventim' && (
                    <button
                      onClick={() => {
                        setEnrichmentEntityId(venue.id);
                        setEnrichmentType('eventim');
                        setShowEnrichmentEditor(true);
                      }}
                      className="btn-sm btn-enrichment"
                    >
                      Edit EventIM
                    </button>
                  )}
                  {!venue.source && (
                    <>
                      <button
                        onClick={() => {
                          setEnrichmentEntityId(venue.id);
                          setEnrichmentType('ticketmaster');
                          setShowEnrichmentEditor(true);
                        }}
                        className="btn-sm btn-enrichment"
                      >
                        Add Ticketmaster
                      </button>
                      <button
                        onClick={() => {
                          setEnrichmentEntityId(venue.id);
                          setEnrichmentType('etix');
                          setShowEnrichmentEditor(true);
                        }}
                        className="btn-sm btn-enrichment"
                      >
                        Add Etix
                      </button>
                      <button
                        onClick={() => {
                          setEnrichmentEntityId(venue.id);
                          setEnrichmentType('eventim');
                          setShowEnrichmentEditor(true);
                        }}
                        className="btn-sm btn-enrichment"
                      >
                        Add EventIM
                      </button>
                    </>
                  )}
                  {/* Scraper-only venues have no supplemental tables (venue_ticketmaster/etix/eventim); do not show Add TM/Etix/EventIM */}
                  {venue.is_deleted ? (
                    <button
                      onClick={() => handleHardDelete(venue.id)}
                      className="btn-sm btn-danger"
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Hard Delete
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDelete(venue.id)}
                      className="btn-sm btn-danger"
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Delete
                    </button>
                  )}
                  {venue.manually_updated_fields && venue.manually_updated_fields.length > 0 && (
                    <button
                      onClick={() => setExpandedVenueId(expandedVenueId === venue.id ? null : venue.id)}
                      style={{ marginLeft: '0.5rem', fontSize: '0.875rem', padding: '0.25rem 0.5rem' }}
                      className="btn-sm"
                    >
                      {expandedVenueId === venue.id ? 'Hide' : 'Show'} Protected Fields ({venue.manually_updated_fields.length})
                    </button>
                  )}
                </td>
              </tr>
              {expandedVenueId === venue.id && venue.manually_updated_fields && venue.manually_updated_fields.length > 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderTop: '2px solid #ddd' }}>
                    <div style={{ marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Protected Fields ({venue.manually_updated_fields.length})
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
                      {venue.manually_updated_fields.map((update, idx) => (
                        <div key={idx} style={{ padding: '0.75rem', backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#333' }}>{update.field_name}</div>
                          <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
                            <div><strong>Old:</strong> {String(update.old_value)}</div>
                            <div><strong>New:</strong> {String(update.new_value)}</div>
                            <div><strong>Date:</strong> {new Date(update.mod_date).toLocaleString()}</div>
                          </div>
                          <button
                            onClick={() => handleUnlockField(venue.id, update.field_name)}
                            disabled={unlockingField?.venueId === venue.id && unlockingField?.fieldName === update.field_name}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            className="btn-sm"
                          >
                            {unlockingField?.venueId === venue.id && unlockingField?.fieldName === update.field_name ? 'Unlocking...' : 'Unlock'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showEnrichmentEditor && (
        <EnrichmentEditor
          entityType="venue"
          entityId={enrichmentEntityId}
          enrichmentType={enrichmentType}
          onClose={() => {
            setShowEnrichmentEditor(false);
            setEnrichmentEntityId('');
          }}
        />
      )}
    </PageLayout>
  );
};

export default VenuesPage;

